import { ColumnsType } from "antd/es/table";
import { FilterFilled } from "@ant-design/icons";
import classNames from "classnames";
import { UserItem } from "shared/api";
import {
  columnsData,
  OrderingUsersEnum,
  sortDirections,
} from "shared/constants";
import {
  MultipleOptionsTableFilter,
  TableButton,
  TableSearchFilter,
} from "shared/ui";

interface UserGetUsersColumns {
  t: Function;
  editUserHandler: Function;
  showDeleteUserModal: Function;
  showChangePasswordModal: Function;
  changeSort: Function;

  userNameValue: string;
  firstNameValue: string;
  lastNameValue: string;
  emailValue: string;
  rolesValue: string;
  superuserValue: string;

  handleUserNameValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleFirstNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleLastNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRolesChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSuperuserChange: (value: string) => void;

  clearUserNameValue: () => void;
  clearFirstNameValue: () => void;
  clearLastNameValue: () => void;
  clearEmailValue: () => void;
  clearRolesValue: () => void;
  clearSuperuserValue: () => void;
}

export const useGetUsersColumns = ({
  t,
  editUserHandler,
  showDeleteUserModal,
  showChangePasswordModal,
  changeSort,

  userNameValue,
  firstNameValue,
  lastNameValue,
  emailValue,
  rolesValue,
  superuserValue,

  handleUserNameValue,
  handleFirstNameChange,
  handleLastNameChange,
  handleEmailChange,
  handleSuperuserChange,

  handleRolesChange,

  clearUserNameValue,
  clearFirstNameValue,
  clearLastNameValue,
  clearEmailValue,
  clearRolesValue,
  clearSuperuserValue,
}: UserGetUsersColumns): ColumnsType<UserItem> => [
  {
    title: t(columnsData.username.title),
    render: (user: UserItem) => (
      <TableButton
        type="title"
        title={user?.username}
        onClick={() => editUserHandler(user)}
      />
    ),
    sortDirections,
    onHeaderCell: () => ({
      onClick: () =>
        changeSort(
          OrderingUsersEnum.USERNAME,
          OrderingUsersEnum.REVERSE_USERNAME
        ),
    }),
    sorter: true,
    filterIcon: (
      <FilterFilled
        className={classNames({
          "!text-blue-600 scale-125": userNameValue.length > 0,
        })}
      />
    ),
    filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
      <TableSearchFilter
        setSelectedKeys={setSelectedKeys}
        clearFilters={clearUserNameValue}
        confirm={confirm}
        value={userNameValue}
        handleFilterChange={handleUserNameValue}
        visible={visible}
        placeholder={t("Search Username")}
        title={t("Filter by Username")}
      />
    ),
  },
  {
    title: t(columnsData.last_name.title),
    dataIndex: columnsData.last_name.dataIndex,
    key: columnsData.last_name.key,
    sortDirections,
    onHeaderCell: () => ({
      onClick: () =>
        changeSort(
          OrderingUsersEnum.LAST_NAME,
          OrderingUsersEnum.REVERSE_LAST_NAME
        ),
    }),
    sorter: true,
    filterIcon: (
      <FilterFilled
        className={classNames({
          "!text-blue-600 scale-125": lastNameValue.length > 0,
        })}
      />
    ),
    filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
      <TableSearchFilter
        setSelectedKeys={setSelectedKeys}
        clearFilters={clearLastNameValue}
        confirm={confirm}
        value={lastNameValue}
        handleFilterChange={handleLastNameChange}
        visible={visible}
        placeholder={t("Search Last Name")}
        title={t("Filter by Last Name")}
      />
    ),
  },
  {
    title: t(columnsData.first_name.title),
    dataIndex: columnsData.first_name.dataIndex,
    key: columnsData.first_name.key,
    sortDirections,
    onHeaderCell: () => ({
      onClick: () =>
        changeSort(
          OrderingUsersEnum.FIRST_NAME,
          OrderingUsersEnum.REVERSE_FIRST_NAME
        ),
    }),
    sorter: true,
    filterIcon: (
      <FilterFilled
        className={classNames({
          "!text-blue-600 scale-125": firstNameValue.length > 0,
        })}
      />
    ),
    filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
      <TableSearchFilter
        setSelectedKeys={setSelectedKeys}
        clearFilters={clearFirstNameValue}
        confirm={confirm}
        value={firstNameValue}
        handleFilterChange={handleFirstNameChange}
        visible={visible}
        placeholder={t("Search First Name")}
        title={t("Filter by First Name")}
      />
    ),
  },
  {
    title: t(columnsData.email.title),
    dataIndex: columnsData.email.dataIndex,
    key: columnsData.email.key,
    sortDirections,
    onHeaderCell: () => ({
      onClick: () =>
        changeSort(OrderingUsersEnum.EMAIL, OrderingUsersEnum.REVERSE_EMAIL),
    }),
    sorter: true,
    filterIcon: (
      <FilterFilled
        className={classNames({
          "!text-blue-600 scale-125": emailValue.length > 0,
        })}
      />
    ),
    filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
      <TableSearchFilter
        setSelectedKeys={setSelectedKeys}
        clearFilters={clearEmailValue}
        confirm={confirm}
        value={emailValue}
        handleFilterChange={handleEmailChange}
        visible={visible}
        placeholder={t("Search Email")}
        title={t("Filter by Email")}
      />
    ),
  },
  {
    title: t(columnsData.superuser.title),
    dataIndex: columnsData.superuser.dataIndex,
    key: columnsData.superuser.key,
    render: (isSuperuser: boolean) => (isSuperuser ? "Yes" : "No"),
    filterDropdown: ({ clearFilters }) => (
      <MultipleOptionsTableFilter
        options={[
          { label: "Yes", value: "true" },
          { label: "No", value: "false" },
        ]}
        selectedValues={superuserValue ? [superuserValue] : []}
        handleChange={(values) => {
          const first = values?.[0];
          if (first === undefined) {
            clearSuperuserValue();
          } else {
            handleSuperuserChange(String(first)); // true/false
          }
        }}
        clearFilters={clearFilters}
      />
    ),
    filteredValue: superuserValue ? [superuserValue] : null,
  },
  {
    title: t(columnsData.roles.title),
    key: columnsData.roles.key,
    render: (user: UserItem) =>
      user.roles?.length ? user.roles.map((role) => role.name).join(", ") : "",
    filterIcon: (
      <FilterFilled
        className={classNames({
          "!text-blue-600 scale-125": rolesValue.length > 0,
        })}
      />
    ),
    filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
      <TableSearchFilter
        setSelectedKeys={setSelectedKeys}
        clearFilters={clearRolesValue}
        confirm={confirm}
        value={rolesValue}
        handleFilterChange={handleRolesChange}
        visible={visible}
        placeholder={t("Search Roles")}
        title={t("Filter by Roles")}
      />
    ),
  },
  {
    title: t(columnsData.actions.title),
    key: columnsData.actions.key,
    render: (record: UserItem) => {
      return (
        <div className="w-full flex items-center gap-6 justify-center">
          <TableButton
            type="delete"
            onClick={() => showDeleteUserModal(record)}
          />
          <TableButton
            className="text-lg"
            type="password"
            onClick={() => showChangePasswordModal(record.id)}
          />
        </div>
      );
    },
  },
];
